// JQ4-45 · US-AUDIT-005 — resolveSummary
//
// Client-side summary computation for audit log rows.
// Strategy (ADR-3, amended by QA R2-09):
//   1. Translated label `audit-log:actions.<action>` (a drift test fails when an
//      action the backend emits has no label in en AND es)
//   2. Locale-safe fallback for an unknown action: "Action: <code>" /
//      "Acción: <código>" — never an English sentence invented by the humanizer
//      inside a Spanish UI
//   3. Append resourceId suffix when present: "Label — <resourceId>"
//
// Without a `t` (non-React callers/tests) the curated English map and the
// deterministic English humanizer remain the fallback.
//
// Spec refs: R-LIST-02, R-LIST-03, ADR-3.

import { AUDIT_CATEGORY_PREFIXES, type AuditLogEntryResponse } from '@jq4race/shared';

// ── Curated action label map ───────────────────────────────────────────────────

/**
 * Known actions mapped to their curated English labels.
 *
 * IMPORTANT: these labels control what users READ in the audit table.
 * When a new audit action is added to the backend, add a curated entry here
 * for the best UX; the fallback humanizer will still render unknown actions
 * in a reasonable form.
 */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  // auth
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.session.refreshed': 'Session refreshed',
  'auth.session.expired': 'Session expired',
  'auth.password.reset': 'Password reset',
  'auth.mfa.enabled': 'MFA enabled',
  'auth.mfa.disabled': 'MFA disabled',
  'auth.mfa.verified': 'MFA verified',

  // ticket
  'ticket.created': 'Ticket created',
  'ticket.retrieval_requested': 'Retrieval requested',
  'ticket.retrieval_claimed': 'Retrieval claimed',
  'ticket.delivered': 'Ticket delivered',
  'ticket.sms_sent': 'SMS sent',
  'ticket.sms_failed': 'SMS failed',

  // payment
  'payment.cash.registered': 'Cash payment registered',
  'payment.tip.registered': 'Tip registered',
  'payment.comp.registered': 'Complimentary issued',

  // config
  'config.tariff.updated': 'Tariff updated',
  'config.sms_template.upserted': 'SMS template updated',
  'config.sms_template.deleted': 'SMS template deleted',
  'config.location.created': 'Location created',
  'config.location.updated': 'Location updated',
  'config.zone.created': 'Zone created',
  'config.zone.updated': 'Zone updated',
  'config.user.invited': 'User invited',
  'config.user.activated': 'User activated',

  // Real backend action keys (see apps/backend/src/shared/audit/*-audit-actions.ts)
  'auth.login.succeeded': 'Signed in',
  'auth.login.failed': 'Sign-in failed',
  'auth.login.locked': 'Account locked after failed sign-ins',
  'auth.logout.succeeded': 'Signed out',
  'auth.mfa.activated': 'MFA activated',
  'auth.mfa.locked': 'MFA locked',
  'auth.mfa.pending_started': 'MFA setup started',
  'auth.mfa.recovery_used': 'MFA recovery code used',
  'auth.mfa.verify_failed': 'MFA verification failed',
  'auth.mfa.email_otp.requested': 'MFA email code requested',
  'auth.mfa.email_otp.verified': 'MFA email code verified',
  'auth.mfa.email_otp.verify_failed': 'MFA email code verification failed',
  'auth.password_reset.requested': 'Password reset requested',
  'auth.password_reset.completed': 'Password reset completed',
  'auth.refresh_token.lost_response_recovered': 'Session refresh recovered',
  'auth.refresh_token.reuse_detected': 'Session token reuse detected',
  'auth.session.revoked': 'Session revoked',
  'ticket.comp_applied': 'Complimentary applied',
  'ticket.created_offline_late': 'Offline ticket synced late',
  'ticket.reassigned': 'Ticket reassigned',
  'ticket.reopened': 'Ticket reopened',
  'billing.subscription.past_due': 'Subscription marked past due',
  'billing.subscription.suspended': 'Subscription suspended',
  'payment.comp.applied': 'Complimentary applied',
  'payment.manual_subscription.recorded': 'Manual subscription payment recorded',
  'payment.stripe.attempt_initiated': 'Card payment started',
  'payment.stripe.failed': 'Card payment failed',
  'payment.stripe.succeeded': 'Card payment succeeded',
  'payment.tip.included': 'Tip included',
  'config.branding.updated': 'Branding updated',
  'config.checkin.updated': 'Check-in fields updated',
  'config.comps.updated': 'Complimentary settings updated',
  'config.event_tariff.updated': 'Event tariff updated',
  'config.hotel.updated': 'Hotel configuration updated',
  'config.niche.updated': 'Niche template updated',
  'config.ticket_types.updated': 'Ticket types updated',
  'config.tip.updated': 'Tip configuration updated',
  // identity (org-scoped and platform team deactivation / reactivation)
  'iam.user.deactivated': 'User deactivated',
  'iam.user.reactivated': 'User reactivated',
};

// ── Known category prefixes in priority order ─────────────────────────────────

// Derived from the shared category→prefix map so this list never drifts from the
// backend's actual action prefixes (e.g. 'auth.', 'ticket.', 'payment.', 'config.').
const CATEGORY_PREFIXES = Object.values(AUDIT_CATEGORY_PREFIXES);

// ── Humanizer fallback ────────────────────────────────────────────────────────

/**
 * Converts an unknown action string into a readable English sentence.
 *
 * Steps:
 *   1. Strip the longest matching category prefix
 *   2. Replace dots and underscores with spaces
 *   3. Sentence-case the result (capitalize first char, lowercase the rest)
 */
function humanize(action: string): string {
  let text = action;

  // Strip category prefix (e.g. 'ticket.' → '')
  for (const prefix of CATEGORY_PREFIXES) {
    if (text.startsWith(prefix)) {
      text = text.slice(prefix.length);
      break;
    }
  }

  // Replace separators with spaces
  text = text.replace(/[._]/g, ' ');

  // Sentence-case
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Minimal structural type of i18next's `t` — avoids coupling this pure module
 * to a specific namespace. The `audit-log:` prefix is explicit in the lookup,
 * so any bound `t` (audit-log, dashboard, ...) works.
 */
export type SummaryTranslate = (
  key: string,
  options?: { defaultValue?: string; action?: string },
) => string;

/** Sentinel to tell "no such key" apart from a real label (i18next echoes defaultValue). */
const MISSING_LABEL = '__jq4_missing_audit_label__';

/**
 * Label for an audit action: the translated label when `t` is given, with a
 * translated "Action: <code>" for anything unlabeled; without `t`, the curated
 * English label (or the humanizer fallback for unknown actions).
 */
export function resolveActionLabel(action: string, t?: SummaryTranslate): string {
  if (!t) return AUDIT_ACTION_LABELS[action] ?? humanize(action);
  const label = t(`audit-log:actions.${action}`, { defaultValue: MISSING_LABEL });
  if (label !== MISSING_LABEL && typeof label === 'string') return label;
  return t('audit-log:unknownAction', { action, defaultValue: `Action: ${action}` });
}

/**
 * Derives a concise, human-readable summary for an audit log entry.
 *
 * Uses the curated labels first (translated when `t` is provided); falls back to
 * the deterministic humanizer for any action not in the map. Appends
 * " — <resourceId>" when a resourceId is present.
 */
export function resolveSummary(
  entry: Pick<AuditLogEntryResponse, 'action' | 'resourceId'>,
  t?: SummaryTranslate,
): string {
  const base = resolveActionLabel(entry.action, t);

  if (entry.resourceId) {
    return `${base} — ${entry.resourceId}`;
  }
  return base;
}
